import { useContext, useEffect, useState } from 'react';
import { ApplicationContext } from '../../../contexts/ApplicationContext';
import { CheckoutLoadingSkeleton } from '../components/LoadingSkeleton';
import { apiFactory } from '../../../api/ApiFactory';
import {
	CONTABILIZEI_API_KEY,
	CONTABILIZEI_REGISTER_URL
} from '../components/constants/contabilizei';

interface IBlockedLinkProps {
	/**
	 * The single-use link check hasn't answered yet. The component is already
	 * mounted at this point — it holds the skeleton and warms up the api key
	 * so that, when the link turns out to be invalid, the redirect fires
	 * immediately instead of after a second loading pass.
	 */
	isResolving?: boolean;
}

export const BlockedLink = ({ isResolving = false }: IBlockedLinkProps) => {
	const { customization } = useContext(ApplicationContext);

	// `null` = still resolving; redirecting before it settles would send a
	// Contabilizei buyer to /login.
	const [apiKey, setApiKey] = useState<string | null>(null);

	useEffect(() => {
		let isMounted = true;

		apiFactory.getApiKey().then((key) => {
			if (!isMounted) return;

			// On the unified checkout domain the key only exists after the
			// page has resolved `/platforms/slug/:slug` and called
			// `enableCustom`. While the gate is still running, an empty key
			// means "not registered yet", not "no key" — keep waiting and
			// read it again when `isResolving` flips.
			if (!key && isResolving) return;

			setApiKey(key);
		});

		return () => {
			isMounted = false;
		};
	}, [isResolving]);

	const isContabilizei = apiKey === CONTABILIZEI_API_KEY;

	useEffect(() => {
		// Nothing is blocked yet — the link may still turn out to be valid.
		if (isResolving) return;
		if (apiKey === null) return;

		// Contabilizei redirects to its own branded domain, so it must not
		// wait on `customization.url` — that one still points at
		// contabilizei.curseduca.pro.
		if (isContabilizei) {
			location.replace(CONTABILIZEI_REGISTER_URL);
			return;
		}

		if (!customization?.url) return;

		const baseUrl = customization.url.replace(/\/+$/, '');

		location.replace(`${baseUrl}/login`);
	}, [isResolving, apiKey, isContabilizei, customization?.url]);

	// Hold the skeleton while the gate/api key are still resolving and on
	// every path that ends in a redirect, so neither the checkout form nor
	// the "link inválido" card ever flashes before location.replace() takes
	// effect.
	const isRedirecting = isResolving || apiKey === null || isContabilizei;

	if (isRedirecting || !customization || customization.url) {
		return <CheckoutLoadingSkeleton />;
	}

	return (
		<div
			className="container d-flex align-items-center justify-content-center"
			style={{ height: '100vh', width: '600px' }}
		>
			<div className="card card-custom my-auto">
				<div className="card-body">
					<div className="text-center">
						<h1 className="display-3 text-muted font-weight-bold">
							🔒
						</h1>
						<h1 className="font-size-h3 text-dark-75 font-weight-bold">
							Link inválido
						</h1>
						<h1 className="font-size-h5 text-dark-75 font-weight-bold">
							Este link de checkout não está mais disponível
						</h1>
						<p className="text-muted font-size-lg font-weight-bold">
							O link pode ter expirado ou já ter sido utilizado.
							Solicite um novo link ao administrador da plataforma.{' '}
							{customization?.url && (
								<a
									className="text-primary"
									href={customization.url}
								>
									Clique aqui para acessar a plataforma
								</a>
							)}
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};
